import { styled, TextField, type TextFieldProps } from '@mui/material';
import {
  Controller,
  type Control,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';

type FormFieldProps<T extends FieldValues> = Omit<
  TextFieldProps,
  'name' | 'error' | 'helperText'
> & {
  readonly name: FieldPath<T>;
  readonly control: Control<T>;
  readonly label: string;
  readonly errors?: FieldErrors<T>;
  readonly type?: string;
};

export const StyledTextField = styled(TextField)({
  '& .MuiInputBase-input': {
    boxSizing: 'border-box',
    height: 48,
  },
});

const FormField = <T extends FieldValues>({
  name,
  control,
  label,
  errors,
  type = 'text',
  ...textFieldProps
}: FormFieldProps<T>) => {
  const fieldError = errors?.[name];
  const errorMessage =
    fieldError && typeof fieldError === 'object' && 'message' in fieldError
      ? String(fieldError.message)
      : undefined;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <StyledTextField
          {...field}
          {...textFieldProps}
          fullWidth
          required={textFieldProps.required ?? true}
          error={Boolean(fieldError)}
          helperText={errorMessage}
          label={label}
          placeholder={textFieldProps.placeholder ?? label}
          slotProps={{ inputLabel: { shrink: true } }}
          type={type}
        />
      )}
    />
  );
};

export default FormField;
